import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTenantHref } from '../lib/tenantPath.jsx';

const REFRESH_INTERVAL_MS = 60_000;

export default function PatientList() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const [fhirByPatient, setFhirByPatient] = useState({});
  const [fhirFetchedAt, setFhirFetchedAt] = useState(null);
  const [fhirLoading, setFhirLoading] = useState(false);
  const [fhirError, setFhirError] = useState(null);
  const intervalRef = useRef(null);

  async function loadPatients() {
    const r = await api.patients({ search, status });
    setPatients(r.patients);
  }

  async function refreshFhir() {
    setFhirLoading(true); setFhirError(null);
    try {
      const r = await api.prescribersSummary();
      const map = {};
      for (const row of r.patients) map[row.patient_id] = row;
      setFhirByPatient(map);
      setFhirFetchedAt(new Date(r.fetched_at));
    } catch (e) {
      setFhirError(e.message);
    } finally {
      setFhirLoading(false);
    }
  }

  useEffect(() => { loadPatients().catch(console.error); }, [search, status]);

  useEffect(() => {
    refreshFhir();
    intervalRef.current = setInterval(refreshFhir, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold">Patients</h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <FhirStatus fetchedAt={fhirFetchedAt} loading={fhirLoading} error={fhirError} onRefresh={refreshFhir} />
          <input className="input w-full sm:w-64" placeholder="Search name or MRN..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input w-full sm:w-44" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="admitted">Admitted</option>
            <option value="pre-discharge">Pre-discharge</option>
            <option value="discharged">Discharged</option>
          </select>
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Patient</th>
              <th className="text-left px-2">Room</th>
              <th className="text-left px-2">Discharge</th>
              <th className="text-left px-2">Rx</th>
              <th className="text-left px-2">Prescriber(s)</th>
              <th className="text-left px-2">Alerts</th>
              <th className="text-left px-2">M2B</th>
              <th className="text-left px-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {patients.map(p => (
              <PatientRow key={p.id} p={p} fhir={fhirByPatient[p.id]} />
            ))}
            {patients.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">No patients</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function PatientRow({ p, fhir }) {
  // Phase 8.4 — call the hook inside the component that uses it.  The
  // outer PatientList is a separate function, so its tHref isn't in scope
  // here once the bundler does its thing.
  const tHref = useTenantHref();
  const live = fhir?.prescribers || [];
  const liveCount = fhir?.prescriber_count ?? p.prescriber_count ?? 0;
  return (
    <tr className="hover:bg-slate-50 align-top">
      <td className="px-4 py-2">
        <Link to={tHref(`/patients/${p.id}`)} className="hover:underline font-medium" style={{ color: 'var(--brand-primary)' }}>
          {p.first_name} {p.last_name}
        </Link>
        <div className="text-xs text-slate-500">{p.mrn}</div>
      </td>
      <td className="px-2 py-2">{p.room_number || '—'}</td>
      <td className="px-2 py-2">{p.discharge_planned_at ? new Date(p.discharge_planned_at).toLocaleString() : '—'}</td>
      <td className="px-2 py-2">
        <span className="badge-success mr-1">{p.rx_ready}✓</span>
        {p.rx_rejected > 0 && <span className="badge-critical mr-1">{p.rx_rejected}✗</span>}
        <span className="badge-neutral">{p.rx_count}</span>
      </td>
      <td className="px-2 py-2">
        <PrescriberCell live={live} fallback={p.primary_prescriber} count={liveCount} />
      </td>
      <td className="px-2 py-2">{p.open_alerts > 0 ? <span className="badge-warning">{p.open_alerts}</span> : '—'}</td>
      <td className="px-2 py-2">{p.m2b_eligible ? <span className="badge-success">Eligible</span> : <span className="badge-neutral">No</span>}</td>
      <td className="px-2 py-2"><span className="badge-neutral">{p.status}</span></td>
    </tr>
  );
}

function PrescriberCell({ live, fallback, count }) {
  if (live && live.length > 0) {
    const first = live[0];
    return (
      <div className="text-sm">
        <div className="font-medium leading-tight">{first.name}</div>
        <div className="text-xs text-slate-500 leading-tight">
          {first.specialty?.[0] || ''}
          {first.npi && <> · NPI <span className="font-mono">{first.npi}</span></>}
        </div>
        {count > 1 && (
          <div className="text-xs text-slate-500 mt-0.5">+{count - 1} more</div>
        )}
      </div>
    );
  }
  if (fallback?.name) {
    return (
      <div className="text-sm">
        <div className="leading-tight">{fallback.name}</div>
        <div className="text-xs text-slate-400">awaiting FHIR…</div>
      </div>
    );
  }
  return <span className="text-slate-400">—</span>;
}

function FhirStatus({ fetchedAt, loading, error, onRefresh }) {
  const ago = useTimeAgo(fetchedAt);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-2 h-2 rounded-full ${error ? 'bg-red-500' : loading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`} />
      <span className="text-slate-600">
        {error ? <span className="text-red-600">FHIR error: {error}</span>
          : loading ? 'Refreshing FHIR…'
          : fetchedAt ? <>Live FHIR · updated {ago}</>
          : 'Connecting to FHIR…'}
      </span>
      <button onClick={onRefresh} className="text-slate-500 hover:underline" disabled={loading}>↻</button>
    </div>
  );
}

function useTimeAgo(d) {
  const [, force] = useState(0);
  useEffect(() => {
    const i = setInterval(() => force(x => x + 1), 1000);
    return () => clearInterval(i);
  }, []);
  if (!d) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return d.toLocaleTimeString();
}
